import type { SerialConnectOptions, SerialTransport } from "./serial-transport";

type PendingRead = {
  length: number;
  resolve: (value: Uint8Array) => void;
  reject: (error: Error) => void;
  timeoutId: number;
};

function canIgnoreSignalSetError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  const message = error.message.toLowerCase();
  return message.includes("not supported") || message.includes("cannot set");
}

function concatBytes(a: Uint8Array<ArrayBufferLike>, b: Uint8Array<ArrayBufferLike>): Uint8Array<ArrayBufferLike> {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

function buildPortLabel(port: SerialPort): string {
  const info = port.getInfo();
  if (info.usbVendorId !== undefined || info.usbProductId !== undefined) {
    const vendor = info.usbVendorId?.toString(16).toUpperCase().padStart(4, "0") ?? "????";
    const product = info.usbProductId?.toString(16).toUpperCase().padStart(4, "0") ?? "????";
    return `USB serial device (${vendor}:${product})`;
  }
  return "Selected serial device";
}

/** Browser implementation of the shared serial transport contract. */
export class WebSerialTransport implements SerialTransport {
  private selectedPort: SerialPort | null = null;
  private port: SerialPort | null = null;
  private readBuffer: Uint8Array<ArrayBufferLike> = new Uint8Array(0);
  private pendingReads: PendingRead[] = [];
  private disconnectHandler: ((reason: string) => void) | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private readLoopPromise: Promise<void> | null = null;
  private opening = false;
  private connected = false;
  private closedByUs = false;
  private readonly handleDisconnectEvent = (event: SerialPortConnectionEvent) => {
    if (this.port && event.port === this.port && !this.closedByUs) {
      this.handleDisconnect("Serial device disconnected");
    }
  };

  constructor() {
    if (typeof navigator !== "undefined" && navigator.serial) {
      navigator.serial.addEventListener("disconnect", this.handleDisconnectEvent);
    }
  }

  setDisconnectHandler(handler: ((reason: string) => void) | null): void {
    this.disconnectHandler = handler;
  }

  async connect(_portPath?: string, options?: SerialConnectOptions): Promise<{ portLabel: string }> {
    if (this.connected && this.port) {
      return { portLabel: buildPortLabel(this.port) };
    }
    if (this.opening) {
      throw new Error("Serial connection already in progress");
    }
    if (!("serial" in navigator)) {
      throw new Error("Web Serial is not available in this browser");
    }

    this.opening = true;
    this.closedByUs = false;

    try {
      if (options?.forcePortSelection) {
        // Bootloaders may enumerate as a different USB serial device than the
        // normal receiver firmware, so flashing must not reuse that selection.
        this.selectedPort = await navigator.serial.requestPort();
      } else if (!this.selectedPort) {
        this.selectedPort = await this.selectPort();
      }

      this.port = this.selectedPort;
      await this.port.open({
        baudRate: 38400,
        dataBits: 8,
        stopBits: 1,
        parity: "none",
        flowControl: "none"
      });

      if (this.port.setSignals) {
        try {
          await this.port.setSignals({
            dataTerminalReady: true,
            requestToSend: false
          });
        } catch (error) {
          if (!canIgnoreSignalSetError(error)) {
            throw error;
          }
        }
      }

      this.connected = true;
      this.readBuffer = new Uint8Array(0);
      this.startReadLoop();
      return { portLabel: buildPortLabel(this.port) };
    } catch (error) {
      this.port = null;
      this.connected = false;
      this.selectedPort = null;
      throw error;
    } finally {
      this.opening = false;
    }
  }

  async disconnect(): Promise<void> {
    this.closedByUs = true;
    this.rejectPendingReads(new Error("Serial port disconnected"));
    this.readBuffer = new Uint8Array(0);

    const port = this.port;
    this.port = null;
    this.connected = false;

    if (this.reader) {
      try {
        await this.reader.cancel();
      } catch {
        // Ignore cancel errors during disconnect.
      }
      try {
        this.reader.releaseLock();
      } catch {
        // Ignore release errors during disconnect.
      }
      this.reader = null;
    }

    await this.readLoopPromise?.catch(() => {});
    this.readLoopPromise = null;

    if (port) {
      await port.close().catch(() => {});
    }
  }

  async readExact(length: number, timeoutMs = 2500): Promise<Uint8Array> {
    if (!Number.isInteger(length) || length < 0) {
      throw new Error("read length must be a non-negative integer");
    }
    if (!this.connected || !this.port) {
      throw new Error("Serial port is not connected");
    }
    if (length === 0) {
      return new Uint8Array(0);
    }

    if (this.readBuffer.length >= length) {
      const out = this.readBuffer.slice(0, length);
      this.readBuffer = this.readBuffer.slice(length);
      return out;
    }

    return new Promise<Uint8Array>((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        this.pendingReads = this.pendingReads.filter((pending) => pending !== request);
        reject(new Error(`Timed out waiting for ${length} bytes`));
      }, timeoutMs);

      const request: PendingRead = {
        length,
        timeoutId,
        resolve: (value) => {
          window.clearTimeout(timeoutId);
          resolve(value);
        },
        reject: (error) => {
          window.clearTimeout(timeoutId);
          reject(error);
        }
      };
      this.pendingReads.push(request);
    });
  }

  async readExactOrNull(length: number, timeoutMs = 2500): Promise<Uint8Array | null> {
    try {
      return await this.readExact(length, timeoutMs);
    } catch (error) {
      if (error instanceof Error && error.message.startsWith("Timed out waiting for ")) {
        return null;
      }
      throw error;
    }
  }

  async writeAll(payload: Uint8Array): Promise<void> {
    if (!this.port?.writable || !this.connected) {
      throw new Error("Serial port is not connected");
    }
    const writer = this.port.writable.getWriter();
    try {
      await writer.write(payload);
    } finally {
      writer.releaseLock();
    }
  }

  async clearInputBuffer(): Promise<void> {
    this.readBuffer = new Uint8Array(0);
  }

  private startReadLoop(): void {
    if (!this.port?.readable) {
      throw new Error("Selected serial port is not readable");
    }
    this.reader = this.port.readable.getReader();
    this.readLoopPromise = this.runReadLoop(this.reader);
  }

  private async selectPort(): Promise<SerialPort> {
    const grantedPorts = await navigator.serial.getPorts?.();
    if (grantedPorts && grantedPorts.length > 0) {
      return grantedPorts[0];
    }
    return navigator.serial.requestPort();
  }

  private async runReadLoop(reader: ReadableStreamDefaultReader<Uint8Array>): Promise<void> {
    try {
      while (this.port && this.connected) {
        const result = await reader.read();
        if (result.done) {
          break;
        }
        if (result.value && result.value.length > 0) {
          const chunk = Uint8Array.from(result.value);
          this.readBuffer = concatBytes(this.readBuffer, chunk) as Uint8Array;
          this.flushPendingReads();
        }
      }
      if (!this.closedByUs) {
        this.handleDisconnect("Serial device disconnected");
      }
    } catch (error) {
      if (!this.closedByUs) {
        this.handleDisconnect(error instanceof Error ? error.message : "Serial error");
      }
    } finally {
      try {
        reader.releaseLock();
      } catch {
        // Ignore reader release errors after disconnect.
      }
      if (this.reader === reader) {
        this.reader = null;
      }
    }
  }

  private flushPendingReads(): void {
    while (this.pendingReads.length > 0) {
      const pending = this.pendingReads[0];
      if (this.readBuffer.length < pending.length) {
        return;
      }
      this.pendingReads.shift();
      const out = this.readBuffer.slice(0, pending.length);
      this.readBuffer = this.readBuffer.slice(pending.length);
      pending.resolve(out);
    }
  }

  private rejectPendingReads(error: Error): void {
    const pending = this.pendingReads.splice(0, this.pendingReads.length);
    for (const read of pending) {
      read.reject(error);
    }
  }

  private handleDisconnect(reason: string): void {
    this.connected = false;
    this.port = null;
    this.selectedPort = null;
    this.readBuffer = new Uint8Array(0);
    this.rejectPendingReads(new Error(reason));
    this.disconnectHandler?.(reason);
  }
}
